// src/lib/waba.js
//
// The registered WhatsApp Business Accounts, and the dropdown that picks one.
//
// Every page needs the same list and the same *choice*: one hook and one
// component here so the label an operator sees for an account is the same
// everywhere, so a page cannot quietly send from the default because its own
// fetch failed, and so the account picked in the header is the account every
// page reads and writes.
//
// The list is small, changes rarely, and every page wants it on mount, so it is
// cached module-wide for the life of the tab. `refreshAccounts()` drops the
// cache — Settings calls it after adding or removing one.

import { useCallback, useEffect, useState } from 'react';
import { apiFetch } from './api';

let cache = null;         // { accounts, default } once loaded
let inFlight = null;      // dedupes concurrent mounts into one request

export function refreshAccounts() {
  cache = null;
  inFlight = null;
}

export async function fetchAccounts() {
  if (cache) return cache;
  if (!inFlight) {
    inFlight = apiFetch('/waba-accounts')
      .then((body) => {
        cache = {
          accounts: Array.isArray(body?.accounts) ? body.accounts : [],
          default: body?.default || '',
        };
        return cache;
      })
      .finally(() => { inFlight = null; });
  }
  return inFlight;
}

// ── The current account, shared by every page ────────────────────────────────
//
// The platform is an aggregator: several unrelated businesses, one dashboard.
// So "which account am I looking at" is a property of the session, not of the
// page — switching it in the header changes the analytics, the logs, the
// media library, the campaigns and the inbox together, and a page opened next
// shows the same account the last one did. The choice is remembered per
// browser so a reload does not silently drop the operator back to the default.
const STORAGE_KEY = 'maa.waba_account';
let selectedName = '';
const listeners = new Set();

function readStored() {
  try { return localStorage.getItem(STORAGE_KEY) || ''; } catch { return ''; }
}
function writeStored(name) {
  try {
    if (name) localStorage.setItem(STORAGE_KEY, name);
    else localStorage.removeItem(STORAGE_KEY);
  } catch { /* private mode, blocked storage: the in-memory value still works */ }
}

export function currentAccount() {
  return selectedName;
}

export function selectAccount(name) {
  const next = name || '';
  if (next === selectedName) return;
  selectedName = next;
  writeStored(next);
  listeners.forEach((fn) => fn(next));
}

/**
 * The account list, plus the currently selected name — the same selection on
 * every page that calls this.
 *
 * Selection starts at what this browser last chose if that account still
 * exists, else the platform default, which is what every page did implicitly
 * before there was more than one account.
 */
export function useWabaAccounts() {
  const [accounts, setAccounts] = useState([]);
  const [selected, setSelectedState] = useState(selectedName);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fn = (name) => setSelectedState(name);
    listeners.add(fn);
    return () => listeners.delete(fn);
  }, []);

  const load = useCallback(async ({ force = false } = {}) => {
    if (force) refreshAccounts();
    setLoading(true);
    setError('');
    try {
      const out = await fetchAccounts();
      setAccounts(out.accounts);
      // Keep an existing choice if it still exists; a refresh after an unrelated
      // edit should not silently move the operator to the default.
      const wanted = selectedName || readStored();
      if (wanted && out.accounts.some((a) => a.name === wanted)) {
        selectAccount(wanted);
      } else {
        selectAccount(out.default || out.accounts[0]?.name || '');
      }
      setSelectedState(selectedName);
    } catch (e) {
      setError(e.message || 'Could not load WhatsApp accounts.');
      setAccounts([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const account = accounts.find((a) => a.name === selected) || null;

  return { accounts, selected, setSelected: selectAccount, account, loading, error, reload: load };
}

/** Append `?waba_account=…` to an API path, skipping it when nothing is chosen. */
export function withAccount(path, name) {
  if (!name) return path;
  const sep = path.includes('?') ? '&' : '?';
  return `${path}${sep}waba_account=${encodeURIComponent(name)}`;
}

export function accountLabel(a) {
  if (!a) return '';
  return a.label ? `${a.label} (${a.name})` : a.name;
}

/**
 * The account picker.
 *
 * Renders even when there is exactly one account. Showing which WhatsApp number
 * a template list or a campaign belongs to is worth a line of UI on its own —
 * hiding the control until a second account exists is how someone ends up
 * unsure which number a message went out from.
 */
export function WabaSelect({
  value,
  onChange,
  accounts = [],
  loading = false,
  error = '',
  label = 'WhatsApp Account',
  hint = '',
  disabled = false,
  className = '',
  id = 'waba-account',
}) {
  return (
    <div className={`space-y-1 ${className}`}>
      <label htmlFor={id} className="block text-sm font-medium text-gray-700">
        {label}
      </label>
      <select
        id={id}
        value={value || ''}
        disabled={disabled || loading || !accounts.length}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-lg border border-gray-200 px-3 py-2 focus:ring-2 focus:ring-brand-500 disabled:bg-gray-50 disabled:text-gray-500"
      >
        {loading && <option value="">Loading accounts…</option>}
        {!loading && !accounts.length && <option value="">No accounts configured</option>}
        {!loading &&
          accounts.map((a) => (
            <option key={a.name} value={a.name}>
              {accountLabel(a)}
              {a.token_set ? '' : ' — no token'}
            </option>
          ))}
      </select>
      {error && <p className="text-xs text-red-600">{error}</p>}
      {!error && !loading && !accounts.length && (
        <p className="text-xs text-amber-700">
          Add one under Settings → WhatsApp Business Accounts.
        </p>
      )}
      {!error && hint && <p className="text-xs text-gray-500">{hint}</p>}
    </div>
  );
}


/**
 * The header switcher: the one control that sets the session's account.
 *
 * Compact on purpose — it sits beside the user's name — and it always shows
 * the label so the operator can see at a glance which business the screen
 * belongs to. With a single account it still renders, as a statement of
 * whose data this is.
 */
export function WabaSwitcher({ className = '' }) {
  const { accounts, selected, setSelected, loading, error } = useWabaAccounts();
  return (
    <label className={`flex items-center gap-2 text-sm ${className}`} title={error || 'WhatsApp account this dashboard is showing'}>
      <span className="hidden whitespace-nowrap text-gray-500 sm:inline">Account</span>
      <select
        aria-label="WhatsApp account"
        value={selected || ''}
        disabled={loading || !accounts.length}
        onChange={(e) => setSelected(e.target.value)}
        className="max-w-[16rem] rounded-lg border border-gray-300 bg-white px-2 py-1.5 text-sm text-gray-800 focus:ring-2 focus:ring-brand-500 disabled:bg-gray-50 disabled:text-gray-500"
      >
        {loading && <option value="">Loading…</option>}
        {!loading && !accounts.length && <option value="">No accounts</option>}
        {!loading && accounts.map((a) => (
          <option key={a.name} value={a.name}>{accountLabel(a)}</option>
        ))}
      </select>
    </label>
  );
}
